import { useEffect, useState } from 'react';
import { financialOverviewApi, type FinancialOverview } from './financial-overview-api';
import { FinancialPilotagePreview } from './FinancialPilotagePreview';
import { FinancialPilotageReal } from './FinancialPilotageReal';
import { RemunerationGoalDialog } from './RemunerationGoalDialog';
import { RevenueGoalDialog } from './RevenueGoalDialog';
import { TreasurySettingsDialog } from './TreasurySettingsDialog';
import { FinanceAnchors } from './FinanceAnchors';
import { FinanceLoadingForge } from './FinanceLoadingForge';

function currentParisMonth(): string {
  return new Date().toLocaleDateString('en-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
  });
}

/** Conversion exacte du champ euro : 19,99 ne doit pas être rejeté à cause
 * d'un produit flottant 19.99 * 100 légèrement inférieur à 1999. */
export function salaryGoalCents(draft: string): string | null {
  if (!/^\d+(?:\.\d{1,2})?$/.test(draft)) return null;
  const [euros, decimals = ''] = draft.split('.');
  const cents = BigInt(euros!) * 100n + BigInt(decimals.padEnd(2, '0'));
  return cents >= 100n && cents <= 1000000000n ? cents.toString() : null;
}

/** L'écran XD est le périmètre visuel. Les sources restent en API et dans les
 * titres accessibles, sans anciens graphiques ni panneaux techniques ajoutés. */
export function FinancialOverviewPanel({
  csrfToken,
}: {
  readonly onOpenConnections: () => void;
  readonly csrfToken: string;
}): React.JSX.Element {
  const [month, setMonth] = useState(currentParisMonth);
  const [preview, setPreview] = useState(false);
  const [demoAllowed, setDemoAllowed] = useState(false);
  const [overview, setOverview] = useState<FinancialOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalKind, setGoalKind] = useState<'salary' | 'revenue'>('salary');
  const [draft, setDraft] = useState('');
  const [pointDraft, setPointDraft] = useState('');
  const [goalError, setGoalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [treasuryOpen, setTreasuryOpen] = useState(false);
  const [revenueMonth, setRevenueMonth] = useState(currentParisMonth);
  const [revenueOverview, setRevenueOverview] = useState<FinancialOverview | null>(null);
  const [revenueError, setRevenueError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    void financialOverviewApi.readDemoPermission().then(
      ({ enabled }) => {
        if (active) {
          setDemoAllowed(enabled);
          if (!enabled) setPreview(false);
        }
      },
      () => {
        if (active) {
          setDemoAllowed(false);
          setPreview(false);
        }
      },
    );
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (preview) return;
    setRevenueError(null);
    if (revenueMonth === month) {
      setRevenueOverview(null);
      return;
    }
    let active = true;
    setRevenueError(null);
    setRevenueOverview(null);
    void financialOverviewApi.read(revenueMonth).then(
      (value) => {
        if (active) setRevenueOverview(value);
      },
      (reason) => {
        if (active)
          setRevenueError(reason instanceof Error ? reason.message : 'Le CA est indisponible.');
      },
    );
    return () => {
      active = false;
    };
  }, [revenueMonth, month, preview]);
  useEffect(() => {
    if (preview) return;
    let active = true;
    setLoading(true);
    setError(null);
    setOverview(null);
    void financialOverviewApi.read(month).then(
      (value) => {
        if (active) {
          setOverview(value);
          setLoading(false);
        }
      },
      (reason: unknown) => {
        if (active) {
          setError(
            reason instanceof Error ? reason.message : 'Le bilan financier est indisponible.',
          );
          setLoading(false);
        }
      },
    );
    return () => {
      active = false;
    };
  }, [month, preview]);
  async function saveGoal(): Promise<void> {
    const cents = salaryGoalCents(draft);
    const pointCents = salaryGoalCents(pointDraft);
    if (cents === null || (goalKind === 'revenue' && pointCents === null)) {
      setGoalError('Saisis un montant positif en euros.');
      return;
    }
    setSaving(true);
    setGoalError(null);
    try {
      if (goalKind === 'revenue' && pointCents !== null)
        await financialOverviewApi.saveRevenueSettings(cents, pointCents, csrfToken);
      else await financialOverviewApi.saveGoal(goalKind, cents, csrfToken);
      setOverview(await financialOverviewApi.read(month));
      if (goalKind === 'revenue') setRevenueOverview(await financialOverviewApi.read(revenueMonth));
      setGoalOpen(false);
    } catch (reason) {
      setGoalError(reason instanceof Error ? reason.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="app-content financial-overview finance-xd">
      <header className="finance-xd__heading client-directory__heading">
        <div>
          <p className="client-directory__eyebrow">CHIFFRES CLÉS</p>
          <h1>Finances &amp; Stats</h1>
        </div>
        <FinanceAnchors />
        {demoAllowed ? (
          <div className="finance-demo">
            <span>DONNÉES DE DÉMO</span>
            <button
              type="button"
              role="switch"
              aria-label="Données de démo"
              aria-checked={preview}
              className="financial-overview__preview-toggle"
              onClick={() => {
                setGoalOpen(false);
                setTreasuryOpen(false);
                setPreview(!preview);
              }}
            >
              <i />
            </button>
            <div>
              <span>OFF</span>
              <span>ON</span>
            </div>
          </div>
        ) : null}
      </header>
      {demoAllowed && preview ? (
        <FinancialPilotagePreview />
      ) : (
        <>
          {loading ? <FinanceLoadingForge /> : null}
          {error ? (
            <p className="financial-overview__error tosho-feedback is-error" role="alert">
              {error}
            </p>
          ) : null}
          {overview ? (
            <FinancialPilotageReal
              overview={overview}
              csrfToken={csrfToken}
              onPeriod={setMonth}
              revenueOverview={revenueOverview ?? overview}
              revenuePeriod={revenueMonth}
              revenueError={revenueError}
              onRevenuePeriod={setRevenueMonth}
              onTreasurySetup={() => setTreasuryOpen(true)}
              onSaveWorkingCapitalGoal={async (targetCents) => {
                await financialOverviewApi.saveWorkingCapitalAnnualGoal(targetCents, csrfToken);
                setOverview(await financialOverviewApi.read(month));
              }}
              onRevenueGoal={() => {
                setGoalKind('revenue');
                setPointDraft(
                  overview.breakEvenGoalCents == null
                    ? ''
                    : String(Number(overview.breakEvenGoalCents) / 100),
                );
                setDraft(
                  overview.monthlyRevenueGoalCents === null
                    ? ''
                    : String(Number(BigInt(overview.monthlyRevenueGoalCents)) / 100),
                );
                setGoalError(null);
                setGoalOpen(true);
              }}
              onGoal={() => {
                setGoalKind('salary');
                setDraft(
                  overview.salaryGoalCents === null
                    ? ''
                    : String(Number(BigInt(overview.salaryGoalCents)) / 100),
                );
                setGoalError(null);
                setGoalOpen(true);
              }}
            />
          ) : null}
          {treasuryOpen && overview ? (
            <TreasurySettingsDialog
              overview={overview}
              onCancel={() => setTreasuryOpen(false)}
              onSave={async (thresholds, reserveCents) => {
                await financialOverviewApi.saveTreasuryColors(thresholds, csrfToken, reserveCents);
                setOverview(await financialOverviewApi.read(month));
              }}
            />
          ) : null}
          {goalOpen && overview && goalKind === 'revenue' ? (
            <RevenueGoalDialog
              pointDraft={pointDraft}
              onPointDraft={setPointDraft}
              draft={draft}
              onDraft={setDraft}
              onCancel={() => setGoalOpen(false)}
              onSave={() => void saveGoal()}
              saving={saving}
              error={goalError}
            />
          ) : null}
          {goalOpen && overview && goalKind === 'salary' ? (
            <RemunerationGoalDialog
              overview={overview}
              draft={draft}
              onDraft={setDraft}
              onCancel={() => setGoalOpen(false)}
              onSave={() => void saveGoal()}
              saving={saving}
              error={goalError}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
